import React from 'react';
import { Divider, Flex, TextV2 } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import type { AccommodationListingDTO, AccommodationsConsoleState, Filters, RenderListingEntryFn } from '@shared/types/Accommodations.types';
import { ListingEntry } from './components/ListingEntry';
import { CoupleRecommendationsCheck } from './components/CoupleRecommendationsCheck';
import { useAccommodationsListingMobileLayout } from '../../hooks/useAccommodationsListingMobileLayout';
import { SpecialEventRatesTag } from './components/SpecialEventRatesTag';
import DeskTopListingsHeader from './components/DeskTopListingsHeader';
import type { DesktopListingsHeaderExtras } from './components/DeskTopListingsHeader.types';
import { pxToRem } from '@withjoy/joykit/theme';
import { ResetFiltersHeader } from './components/ResetFiltersHeader';
import type { SortOption, HotelSearchSource } from '@shared/constants/accommodations';
import { ErrorState } from '../ErrorState';

type ListingsPanelProps = Readonly<{
  hidePanel?: boolean;
  isLoadingPrices: boolean;
  listings: ReadonlyArray<AccommodationListingDTO>;
  state: AccommodationsConsoleState;
  sortOption: SortOption;
  onSortChange: (sort: SortOption) => void;
  noOfNights: number;
  filters: Filters;
  updateFilters: (filters: Filters) => void;
  hasError: boolean;
  sourceName: HotelSearchSource;
  includeCouplesPicks: boolean;
  renderListingEntry: RenderListingEntryFn;
  currencyCode: string;
  navigatorLanguage: string;
  dynamicPriceMax: number;
  showFundSavingsSort?: boolean;
  accentColor?: string;
  /** Desktop panel width; the map fills the rest. Undefined keeps the shared default. */
  listingsPanelWidth?: string;
  /**
   * Pinned paid-placement unit (the featured-placement A/B test), and how many organic results
   * render above it. Undefined on `control` and for every non-guest scenario, which keeps the
   * loop below byte-for-byte what ships today.
   */
  featuredPlacement?: Readonly<{ content: React.ReactNode; insertAfterIndex: number }>;
  /**
   * The sponsored hotel the guest searched for, drawn as a hero at the very top of the list, ahead of
   * every organic result and of `featuredPlacement`. Only the `row` arm sets it.
   */
  searchedPlacement?: React.ReactNode;
  /** The map area held no stays, so the list fell back to every result and says so. */
  isShowingOutsideViewport?: boolean;
  /** Rendered under the title, above the first hotel. */
  listingsBanner?: React.ReactNode;
  /** The scenario's title, filter toggle and currency picker on the desktop title row. */
  desktopListingsHeader?: DesktopListingsHeaderExtras;
  /** Tighter desktop top and bottom padding, for the hotel pick's toolbar title row. */
  hasCompactPadding?: boolean;
}>;

/** The width every scenario has shipped with; a scenario opts out by passing its own. */
export const DEFAULT_PANEL_WIDTH = pxToRem(821);

/** Desktop top and bottom padding of the list: the shared default, or the tighter one `hasCompactPadding` asks for. */
export const LISTINGS_PANEL_DESKTOP_PADDING_Y = 8;
export const LISTINGS_PANEL_COMPACT_DESKTOP_PADDING_Y = 5;

const Container: React.FC<{ hide?: boolean; useNewMobileLayout?: boolean; panelWidth?: string; hasCompactPadding?: boolean }> = ({
  hide,
  children,
  useNewMobileLayout,
  panelWidth,
  hasCompactPadding,
}) => {
  return (
    <Flex
      height="fit-content"
      flexDirection="column"
      display={hide ? 'none' : 'flex'}
      width={{ _: 'full', md: panelWidth ?? DEFAULT_PANEL_WIDTH }}
      paddingLeft={useNewMobileLayout ? 4 : 6}
      paddingY={{ _: 6, sm2: hasCompactPadding ? LISTINGS_PANEL_COMPACT_DESKTOP_PADDING_Y : LISTINGS_PANEL_DESKTOP_PADDING_Y }}
      paddingRight={{ _: useNewMobileLayout ? 4 : 6, sm2: 8 }}
      rowGap={{ _: 6, sm2: 6 }}
    >
      {children}
    </Flex>
  );
};

const ListingsPanel = (props: ListingsPanelProps) => {
  const {
    hidePanel,
    listings,
    state,
    noOfNights,
    sortOption,
    onSortChange,
    filters,
    updateFilters,
    hasError,
    sourceName,
    includeCouplesPicks,
    renderListingEntry,
    currencyCode,
    navigatorLanguage,
    dynamicPriceMax,
    showFundSavingsSort,
    accentColor,
    listingsPanelWidth,
    featuredPlacement,
    searchedPlacement,
    isShowingOutsideViewport,
    listingsBanner,
    desktopListingsHeader,
    hasCompactPadding,
  } = props;
  const { isMobileOrTablet, shouldUseNewMobileLayout } = useAccommodationsListingMobileLayout();
  const { tq } = useTranslation('accommodationsConsole');

  // Clamped so a short page (filters down to one result) still shows the placement rather than
  // dropping it off the end.
  const featuredInsertIndex = featuredPlacement ? Math.min(featuredPlacement.insertAfterIndex, listings.length) : -1;

  return (
    <Container
      hide={hidePanel}
      useNewMobileLayout={shouldUseNewMobileLayout}
      panelWidth={listingsPanelWidth}
      hasCompactPadding={hasCompactPadding}
    >
      {isMobileOrTablet ? (
        <SpecialEventRatesTag />
      ) : (
        <DeskTopListingsHeader
          sortOption={sortOption}
          onSortChange={onSortChange}
          includeCouplesPicks={includeCouplesPicks}
          includeFundSavings={showFundSavingsSort}
          accentColor={accentColor}
          {...desktopListingsHeader}
        />
      )}
      {hasError ? (
        <ErrorState />
      ) : (
        <>
          {listingsBanner}
          <ResetFiltersHeader
            filters={filters}
            updateFilters={updateFilters}
            currencyCode={currencyCode}
            navigatorLanguage={navigatorLanguage}
            dynamicPriceMax={dynamicPriceMax}
          />
          {/* Ahead of the featured unit: it explains what the whole list below is, paid placement
              included, so it cannot sit underneath the thing it is describing. */}
          {isShowingOutsideViewport && (
            <TextV2
              color="mono12"
              typographyVariant="body1"
            >
              {tq`No stays in this map area — showing all nearby stays. Move or zoom out the map to focus on a different area.`}
            </TextV2>
          )}
          {searchedPlacement}
          {featuredInsertIndex === 0 && featuredPlacement?.content}
          {listings.map((listing, index) => (
            <React.Fragment key={listing.id}>
              {renderListingEntry({ listing, state, noOfNights, sourceName })}
              {shouldUseNewMobileLayout && <Divider />}
              {featuredInsertIndex === index + 1 && featuredPlacement?.content}
            </React.Fragment>
          ))}
        </>
      )}
    </Container>
  );
};

// Takes the same width as the real panel. Without it the skeleton sits at the shared default and
// the whole column jumps sideways the moment listings arrive.
ListingsPanel.Skeleton = ({
  isMobileOrTablet,
  listingsPanelWidth,
  renderListingEntrySkeleton,
  hasCompactPadding,
}: {
  isMobileOrTablet: boolean;
  listingsPanelWidth?: string;
  /** A scenario whose tile has its own shape passes its own card here; omitted keeps the shared one. */
  renderListingEntrySkeleton?: () => React.ReactNode;
  /** As on the list, so the cards do not move when they load. */
  hasCompactPadding?: boolean;
}) => {
  const listingCard = renderListingEntrySkeleton?.() ?? <ListingEntry.Skeleton />;

  return (
    <Container
      panelWidth={listingsPanelWidth}
      hasCompactPadding={hasCompactPadding}
    >
      {isMobileOrTablet ? <SpecialEventRatesTag.Skeleton /> : <CoupleRecommendationsCheck.Skeleton />}
      {[0, 1, 2, 3].map(index => (
        <React.Fragment key={index}>
          {index > 0 && isMobileOrTablet && <Divider />}
          {listingCard}
        </React.Fragment>
      ))}
    </Container>
  );
};

export { ListingsPanel };
